import React, { useState } from 'react'
import { Modal, Button, Checkbox, DatePicker, message } from 'antd'
import './AuditModal.scss'
import moment from 'moment'
import { api } from '../../../../../services'
import { useTranslation } from 'react-i18next'

const actionTypes = [
  { label: 'All', value: 'all' },
  { label: 'Create', value: 'create' },
  { label: 'Modify', value: 'modify' },
  { label: 'View', value: 'view' },
  { label: 'Share', value: 'share' },
  { label: 'Delete', value: 'delete' }
]

const AuditModal = ({ onClose, visible, id }) => {
  const [selectedActions, setSelectedActions] = useState([])
  const [dates, setDates] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleActionChange = checkedValues => {
    // If 'all' is checked and user clicks another, remove 'all' and keep only the new selection
    if (selectedActions.includes('all') && checkedValues.length > 1) {
      const filtered = checkedValues.filter(v => v !== 'all')
      setSelectedActions(filtered)
    } else if (checkedValues.includes('all')) {
      setSelectedActions(['all'])
    } else {
      setSelectedActions(checkedValues)
    }
  }

  const handleDateChange = value => {
    setDates(value)
  }

  const handleExport = async () => {
    setLoading(true)

    const formattedDates = dates.map(d =>
      d ? moment(d).format('YYYY-MM-DD') : null
    )

    try {
      // Process all selected actions sequentially
      for (const actionType of selectedActions) {
        const res = await api.library.generateAuditReport(
          id,
          actionType,
          formattedDates[0],
          formattedDates[1]
        )

        // Convert response to CSV and download
        if (res && res.data) {
          const csvContent = convertToCSV(res.data)
          await downloadCSV(
            csvContent,
            `audit_report_${actionType}_${formattedDates[0]}_${formattedDates[1]}.csv`
          )
        }
      }

      message.success('Audit report exported successfully!')
      handleDiscard()
    } catch (error) {
      console.error('Export error:', error)
      message.error('Failed to export audit report. Please try again.')
      setLoading(false)
    }
  }

  const convertToCSV = data => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      // Return CSV with headers and audit report type when no data
      const headers = ['Type', 'Message']
      const csvHeader = headers.join(',')
      const csvRow = [
        'Audit Report',
        'No data available for the selected criteria'
      ]
      return [csvHeader, csvRow].join('\n')
    }

    // Define headers based on the audit data structure
    const headers = [
      'Timestamp',
      'User',
      'Action',
      'Status',
      'File/Folder',
      'Message'
    ]

    // Create CSV header row
    const csvHeader = headers.join(',')

    // Create CSV data rows
    const csvRows = data.map(row => {
      const values = [
        row.timestamp || '',
        row.user || '',
        row.action || '',
        row.status || '',
        row.file_or_folder || '',
        row.message || ''
      ]

      return values
        .map(value => {
          // Escape commas and quotes in values
          if (
            typeof value === 'string' &&
            (value.includes(',') || value.includes('"'))
          ) {
            return `"${value.replace(/"/g, '""')}"`
          }
          return value || ''
        })
        .join(',')
    })

    return [csvHeader, ...csvRows].join('\n')
  }

  const downloadCSV = (csvContent, filename) => {
    return new Promise(resolve => {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up the URL object after a short delay
        setTimeout(() => {
          URL.revokeObjectURL(url)
          resolve()
        }, 100)
      } else {
        resolve()
      }
    })
  }

  const handleDiscard = () => {
    setLoading(false)
    setSelectedActions([])
    setDates(null)
    onClose && onClose()
  }

  const exportDisabled = !selectedActions.length || !dates || dates.length !== 2

  const { t } = useTranslation()

  return (
    <Modal
      className="audit-modal-container"
      title={null}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      bodyStyle={{ padding: 0 }}
      style={{ top: 40 }}
      centered
    >
      <div className="audit-modal-inner">
        <div className="audit-modal-title">{t('v5:audit_report')}</div>
        <div className="audit-modal-section-title">{t('v5:audit_type')}</div>
        <Checkbox.Group
          value={selectedActions}
          onChange={handleActionChange}
          className="audit-modal-checkbox-group"
        >
          {actionTypes.map(type => (
            <Checkbox key={type.value} value={type.value}>
              {type.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
        <div className="audit-modal-section-title">{t('v4:time_period')}</div>
        <DatePicker.RangePicker
          className="audit-modal-dates"
          value={dates}
          onChange={handleDateChange}
          allowClear
          placeholder={['From Date', 'To Date']}
          format="YYYY-MM-DD"
          picker="day"
          disabledDate={current => {
            const twoYearsAgo = moment()
              .subtract(2, 'years')
              .startOf('day')
            const today = moment().endOf('day')
            return current && (current < twoYearsAgo || current > today)
          }}
        />
        <div className="audit-modal-footer">
          <Button type="default" onClick={handleDiscard} disabled={loading}>
            {t('v4:discard')}
          </Button>
          <Button
            type="primary"
            onClick={handleExport}
            loading={loading}
            disabled={exportDisabled}
          >
            {t('v4:export')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AuditModal
